/**
 * Money handling for OHC.
 *
 * Every amount in this system is an integer number of cents. Floating point is never
 * used for money: 0.1 + 0.2 is not 0.3, and an accounting system that drifts by a cent
 * is an accounting system nobody trusts.
 *
 * Unit costs are deliberately NOT stored. A stock pool keeps its total cost and its
 * quantity, and the average cost is derived when it is needed. Storing a rounded
 * average and then multiplying it back out is how inventory values slowly go wrong.
 */
export const ZERO = 0;
/** Largest amount we accept, about 90 trillion dollars, the safe integer limit. */
const MAX_CENTS = Number.MAX_SAFE_INTEGER;
export class MoneyError extends Error {
}
export function isCents(value) {
    return typeof value === "number" && Number.isSafeInteger(value);
}
/** Wrap an integer that is already in cents. Throws if it is not a safe integer. */
export function cents(value) {
    if (!Number.isSafeInteger(value)) {
        throw new MoneyError(`not a whole number of cents: ${value}`);
    }
    return value;
}
/**
 * Read an amount written by a person or by a spreadsheet.
 *
 * Accepts "120.50", "120.5", "120", "-4.05", "1,234.56", " 12.00 ", and plain numbers
 * that are exact to two decimals. Rejects anything else rather than guessing, because a
 * silently misread figure is worse than a rejected import row.
 */
export function parseMoney(input) {
    if (typeof input === "number") {
        if (!Number.isFinite(input))
            throw new MoneyError(`not a finite amount: ${input}`);
        const scaled = Math.round(input * 100);
        if (Math.abs(input * 100 - scaled) > 1e-6) {
            throw new MoneyError(`amount has more than 2 decimals: ${input}`);
        }
        return cents(scaled);
    }
    const trimmed = input.trim();
    if (trimmed === "")
        throw new MoneyError("empty amount");
    // Commas are only allowed where a thousands separator belongs. "1,234.56" is an
    // amount; "12," is a broken spreadsheet cell and is rejected rather than read as 12.
    if (trimmed.includes(",") && !/^-?\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(trimmed)) {
        throw new MoneyError(`commas are not in thousands positions: ${JSON.stringify(input)}`);
    }
    const text = trimmed.replace(/,/g, "");
    const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(text);
    if (!match)
        throw new MoneyError(`cannot read amount: ${JSON.stringify(input)}`);
    const [, sign, whole, fraction = ""] = match;
    const paddedFraction = fraction.padEnd(2, "0");
    const total = Number(whole) * 100 + Number(paddedFraction);
    if (total > MAX_CENTS)
        throw new MoneyError(`amount is too large: ${input}`);
    return cents(sign === "-" ? -total : total);
}
/** "1234.56" style, no currency sign and no thousands separators. */
export function formatMoney(value) {
    const negative = value < 0;
    const absolute = Math.abs(value);
    const whole = Math.trunc(absolute / 100);
    const fraction = absolute % 100;
    return `${negative ? "-" : ""}${whole}.${String(fraction).padStart(2, "0")}`;
}
/** "1,234.56" style, for screens and printed documents. */
export function formatMoneyWithSeparators(value) {
    const plain = formatMoney(value);
    const negative = plain.startsWith("-");
    const [whole = "0", fraction = "00"] = (negative ? plain.slice(1) : plain).split(".");
    return `${negative ? "-" : ""}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${fraction}`;
}
export function addMoney(...values) {
    let total = 0;
    for (const value of values)
        total += value;
    return cents(total);
}
export function subtractMoney(from, ...values) {
    let total = from;
    for (const value of values)
        total -= value;
    return cents(total);
}
export function negateMoney(value) {
    return cents(-value);
}
/**
 * Multiply an amount by a quantity, rounding half away from zero.
 *
 * Use this for a line total (unit price times quantity). For splitting one amount over
 * several lines, use allocate, which loses nothing.
 */
export function multiplyMoney(value, quantity) {
    if (!Number.isFinite(quantity))
        throw new MoneyError(`not a finite quantity: ${quantity}`);
    const exact = value * quantity;
    const rounded = exact < 0 ? -Math.round(-exact) : Math.round(exact);
    return cents(rounded);
}
/**
 * Split an amount across weights so that the parts add back up to exactly the total.
 *
 * Largest remainder: every part is rounded down first, then the leftover cents are
 * handed out one at a time to the parts with the biggest remainder. Splitting S$10 over
 * three items gives 3.34, 3.33, 3.33, never 3.33 three times with a cent left behind.
 *
 * This is what spreads a pack cost over the cards kept from it, and a delivery charge
 * over the items in a purchase.
 */
export function allocate(total, weights) {
    if (weights.length === 0)
        throw new MoneyError("cannot allocate over no weights");
    if (weights.some((w) => !Number.isFinite(w) || w < 0)) {
        throw new MoneyError("weights must be finite and not negative");
    }
    const weightTotal = weights.reduce((sum, w) => sum + w, 0);
    if (weightTotal === 0) {
        // Nothing to weigh by, so share it out as evenly as the cents allow.
        return allocate(total, weights.map(() => 1));
    }
    const sign = total < 0 ? -1 : 1;
    const absoluteTotal = Math.abs(total);
    const exact = weights.map((w) => (absoluteTotal * w) / weightTotal);
    const floors = exact.map((value) => Math.floor(value));
    let remaining = absoluteTotal - floors.reduce((sum, value) => sum + value, 0);
    const order = exact
        .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
        .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    const parts = [...floors];
    for (const { index } of order) {
        if (remaining <= 0)
            break;
        parts[index] = (parts[index] ?? 0) + 1;
        remaining -= 1;
    }
    return parts.map((part) => cents(sign * part));
}
/**
 * Average cost of one unit, as an exact fraction rather than a rounded amount.
 *
 * Returned as a plain number because it is only ever used for display or for feeding
 * multiplyMoney. The stored truth stays totalCost and quantity.
 */
export function averageUnitCost(totalCost, quantity) {
    if (quantity === 0)
        return 0;
    if (!Number.isFinite(quantity))
        throw new MoneyError(`not a finite quantity: ${quantity}`);
    return totalCost / quantity;
}
/** Cost of taking `quantity` units out of a pool, so the pool never drifts. */
export function costOfUnits(poolCost, poolQuantity, quantity) {
    if (poolQuantity === 0)
        return ZERO;
    if (quantity === poolQuantity)
        return poolCost;
    return multiplyMoney(poolCost, quantity / poolQuantity);
}
//# sourceMappingURL=money.js.map